import { useRef, useState } from 'react';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { Alert, Link, Text } from '@backstage/ui';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderGithubEmuTranslationRef } from '../../translation';
import { CommonStatusAlert } from '../CommonStatusAlert';

type TenantStatus =
  | 'not_in_vw_core'
  | 'pending_cariad_invitation'
  | 'already_in_cariad'
  | 'available';

interface TenantStatusResult {
  status: TenantStatus;
  displayName?: string;
  mail?: string;
}

export interface EmailStatusPickerSchema {
  title?: string;
  description?: string;
}

interface EmailStatusPickerUiSchema {
  'ui:options'?: {
    pendingAcceptanceUrl?: string;
    placeholder?: string;
    vwgroupOnboardingUrl?: string;
  };
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailStatusPicker = (
  props: FieldExtensionComponentProps<string, EmailStatusPickerSchema>,
) => {
  const { onChange, formData, uiSchema, schema, registry, idSchema } = props;

  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { t } = useTranslationRef(scaffolderGithubEmuTranslationRef);

  const [inputValue, setInputValue] = useState(formData ?? '');
  const [tenantStatus, setTenantStatus] = useState<TenantStatusResult | null>(
    null,
  );
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const uiOptions =
    (uiSchema as EmailStatusPickerUiSchema)?.['ui:options'] ?? {};

  const handleChange = (newValue?: string) => {
    requestIdRef.current += 1;
    setInputValue(newValue ?? '');
    setTenantStatus(null);
    setErrorMessage(null);

    if (formData) onChange(undefined);
  };

  const handleBlur = async () => {
    const email = inputValue.trim();

    if (!EMAIL_SHAPE.test(email)) return;
    if (checkingStatus || tenantStatus) return;
    const requestId = ++requestIdRef.current;
    setCheckingStatus(true);
    setErrorMessage(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('githubemu');
      const response = await fetchApi.fetch(
        `${baseUrl}/azure-user-tenant-status?email=${encodeURIComponent(email)}`,
      );

      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error(t('emailStatusPicker.genericError'));
        }
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.message || `Status check failed (${response.status})`,
        );
      }
      const result: TenantStatusResult = await response.json();
      if (requestId !== requestIdRef.current) return;
      setTenantStatus(result);
      onChange(result.status === 'available' ? email : undefined);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t('emailStatusPicker.genericError'),
      );
      onChange(undefined);
    } finally {
      if (requestId === requestIdRef.current) setCheckingStatus(false);
    }
  };

  const getStatusAlert = () => {
    if (checkingStatus) {
      return (
        <Alert
          status="info"
          icon
          title={t('emailStatusPicker.checkingStatus')}
        />
      );
    }

    if (errorMessage) {
      return <Alert status="danger" icon title={errorMessage} />;
    }

    if (!tenantStatus) return null;

    const userLabel = tenantStatus.displayName
      ? `${tenantStatus.displayName} (${tenantStatus.mail})`
      : (tenantStatus.mail ?? inputValue);

    switch (tenantStatus.status) {
      case 'not_in_vw_core':
        return (
          <Alert
            status="danger"
            icon
            title={t('emailStatusPicker.notInVwCore.title', {
              email: inputValue,
            })}
            description={
              <>
                {t('emailStatusPicker.notInVwCore.description')}
                <Link
                  href={uiOptions.vwgroupOnboardingUrl}
                  target="_blank"
                  rel="noopener"
                >
                  <Text variant="body-small" color="info">
                    {t('emailStatusPicker.notInVwCore.onboardingLink')}
                  </Text>
                </Link>
              </>
            }
          />
        );
      case 'already_in_cariad':
        return (
          <Alert
            status="info"
            icon
            title={t('emailStatusPicker.alreadyInCariad.title', {
              user: userLabel,
            })}
            description={t('emailStatusPicker.alreadyInCariad.description')}
          />
        );
      case 'pending_cariad_invitation':
        return (
          <CommonStatusAlert
            pendingInvitationUrl={
              uiOptions.pendingAcceptanceUrl ??
              'https://myapplications.microsoft.com'
            }
            userLabel={userLabel}
          />
        );
      case 'available':
        return (
          <Alert
            status="success"
            icon
            title={t('emailStatusPicker.available.title', {
              email: inputValue,
            })}
          />
        );
      default:
        return null;
    }
  };

  const showHint =
    !checkingStatus && !tenantStatus && !errorMessage && inputValue.length > 0;

  const TextWidget = registry.widgets.TextWidget;
  const DescriptionFieldTemplate = registry.templates.DescriptionFieldTemplate;
  const description = uiSchema?.['ui:description'] ?? schema.description;
  const placeholder = uiSchema?.['ui:placeholder'] ?? schema.placeholder;
  return (
    <>
      <TextWidget
        {...props}
        id={idSchema.$id}
        label={schema.title ?? 'Email'}
        placeholder={placeholder}
        options={uiOptions}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={() => {}}
      />
      {description && (
        <DescriptionFieldTemplate
          id={`${idSchema.$id}__description`}
          description={description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}
      {showHint && (
        <Text variant="body-small" color="warning">
          {t('emailStatusPicker.hint')}
        </Text>
      )}
      <div aria-live="polite">{getStatusAlert()}</div>
    </>
  );
};
